import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { SecureStorage } from '@aparajita/capacitor-secure-storage';
import { LocalStorageService } from '../../security/local-storage/local-storage.service';

/**
 * JWT session storage backed by the OS keystore on native (iOS Keychain /
 * Android EncryptedSharedPreferences) via @aparajita/capacitor-secure-storage.
 *
 * The rest of the app reads the token synchronously from window.localStorage
 * (see ApiKeyInterceptor.getToken and AuthenticationService.getToken). Secure
 * storage is async, so on native we keep window.localStorage as a runtime
 * *mirror*: every write goes to both, and on cold start `rehydrate()` copies the
 * keystore value back into the mirror before the first HTTP call. This gives
 * encrypted-at-rest sessions without touching any synchronous read path.
 *
 * On web, this service is a no-op: the existing localStorage behavior is
 * untouched so the web build is identical.
 */
@Injectable({ providedIn: 'root' })
export class SecureTokenService {
  private static readonly KEY = 'appToken';

  constructor(private readonly localStorage: LocalStorageService) {}

  private get isNative(): boolean {
    return Capacitor.isNativePlatform();
  }

  /** Persist the JWT to the keystore and refresh the localStorage mirror. */
  async setToken(token: string): Promise<void> {
    if (!this.isNative) {
      return;
    }
    // Mirror uses the same JSON-stringified shape the app already writes/reads.
    this.localStorage.set(SecureTokenService.KEY, token);
    try {
      await SecureStorage.set(SecureTokenService.KEY, token);
    } catch (err) {
      console.error('SecureTokenService.setToken failed', err);
    }
  }

  /** Read the JWT from the keystore (native) or the localStorage mirror (web). */
  async getToken(): Promise<string | null> {
    if (!this.isNative) {
      return null;
    }
    try {
      const value = await SecureStorage.get(SecureTokenService.KEY);
      return typeof value === 'string' ? value : null;
    } catch (err) {
      console.error('SecureTokenService.getToken failed', err);
      return null;
    }
  }

  /** Copy the keystore token into the localStorage mirror. Call once at boot. */
  async rehydrate(): Promise<void> {
    if (!this.isNative) {
      return;
    }
    const token = await this.getToken();
    if (token) {
      this.localStorage.set(SecureTokenService.KEY, token);
    }
  }

  /** Clear the session from both the keystore and the mirror. */
  async clear(): Promise<void> {
    if (!this.isNative) {
      return;
    }
    this.localStorage.remove(SecureTokenService.KEY);
    try {
      await SecureStorage.remove(SecureTokenService.KEY);
    } catch (err) {
      console.error('SecureTokenService.clear failed', err);
    }
  }
}
